import { createSelector } from '@reduxjs/toolkit';
import { featureCollection, type LineString } from '@turf/helpers';
import turfLength from '@turf/length';
import { type AnimationPlaybackControls, useAnimate } from 'framer-motion';
import { Feature } from 'geojson';
import isEqual from 'lodash.isequal';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { MapRef } from 'react-map-gl/mapbox';
import logCustomRouteUpdated from '@alltrails/amplitude/events/logCustomRouteUpdated';
import isSafari from '@alltrails/core/utils/isSafari';
import type { LineProperties, MarkerProperties } from '@alltrails/types/CustomRoute';
import useFitBoundsOnce from '../../../hooks/useFitBoundsOnce';
import useMap from '../../../hooks/useMap';
import { RootState, useAppSelector } from '../../../redux';
import { getMarkerSelectionProperties, selectCustomRouteActiveSegment } from '../../../redux/customRoute';
import type { CustomRoutePath, SegmentPolyline } from '../../../types/CustomRoute';
import { getLineColor, getLineOutlineColor } from '../../../utils/customRoutes/colors';
import { getSegmentsBounds } from '../../../utils/customRoutes/getBounds';
import { customRouteActiveSegmentUniqueId } from '../../../utils/layers';
import { PolylineSource } from '../../Polyline';
import { DrawControlsContext } from '../DrawControls/DrawControlsProvider';
import getDistanceInPixels from './getDistanceInPixels';
import getLineStrings from './getLineStrings';

export const UNIQUE_ID = customRouteActiveSegmentUniqueId;
export const DRAW_UNIQUE_ID = `${UNIQUE_ID}-draw`;
export const ANIMATION_UNIQUE_ID = `${UNIQUE_ID}-animation`;

// Map line length in pixels to animation duration in milliseconds
const ANIMATION_LENGTH_DURATIONS = {
  0: 300,
  400: 500,
  800: 700
};

const selectActiveSegmentPolyline = createSelector(
  (state: RootState) => state.customRoute.activeSegmentIndex,
  (state: RootState) => selectCustomRouteActiveSegment(state)?.color,
  (state: RootState) => selectCustomRouteActiveSegment(state)?.paths,
  (activeSegmentIndex, color, paths) => {
    if (activeSegmentIndex === undefined || color === undefined || paths === undefined) {
      return undefined;
    }

    return {
      color,
      paths
    } as SegmentPolyline;
  }
);

const combinedPathsFeature = (segment: SegmentPolyline, pathIndexes: number[]): Feature<LineString, LineProperties> => {
  const combinedPath = segment.paths.reduce(
    (acc, path, index) => {
      if (pathIndexes.includes(index)) {
        acc.points.push(...path.points);
      }
      return acc;
    },
    { points: [] } as CustomRoutePath
  );

  // A valid LineString requires at least 2 points; duplicate a single point to form a zero-length LineString
  if (combinedPath.points.length === 1) {
    combinedPath.points.push(combinedPath.points[0]);
  }

  return getLineStrings({ ...segment, paths: [combinedPath] })[0];
};
const getAnimationDuration = (pathIndexes: number[], segment: SegmentPolyline, map?: MapRef) => {
  let duration = ANIMATION_LENGTH_DURATIONS[0];
  if (!map) {
    return duration;
  }

  const feature = combinedPathsFeature(segment, pathIndexes);
  const lengthMeters = turfLength(feature, { units: 'meters' });
  const lengthPixels = getDistanceInPixels(map, lengthMeters) || 0;
  Object.keys(ANIMATION_LENGTH_DURATIONS).forEach(key => {
    const length = Number(key);
    if (lengthPixels >= length) {
      duration = ANIMATION_LENGTH_DURATIONS[key];
    }
  });

  return duration;
};

/**
 * Render and animate all paths of the active segment
 * NOTE: Implementation assumes that only one action can be performed at a time (e.g. adding or removing a marker, dragging a marker, or free drawing)
 * NOTE: Only one animation can be performed at a time; any action that triggers an animation will cancel any ongoing animation
 */
type ActivePolylineProps = {
  hideArrows?: boolean;
};

const ActivePolyline = ({ hideArrows }: ActivePolylineProps) => {
  const map = useMap();
  const {
    activeSegmentIndex,
    selectedHandleIndex,
    selectedWaypointId,
    segment,
    isSmartRouting,
    routeActivityType,
    undoStackLength,
    redoStackLength,
    reverseCount,
    activeSegmentDndId,
    isInCutMode,
    isTrailMap
  } = useAppSelector(state => ({
    activeSegmentIndex: state.customRoute.activeSegmentIndex,
    selectedHandleIndex: state.customRoute.selectedHandleIndex,
    selectedWaypointId: state.customRoute.selectedWaypointId,
    segment: selectActiveSegmentPolyline(state),
    isSmartRouting: state.customRoute.isSmartRouting,
    routeActivityType: state.customRoute.route.activityType,
    undoStackLength: state.customRoute.undoStack.length,
    redoStackLength: state.customRoute.redoStack.length,
    reverseCount: state.customRoute.reverseCount,
    activeSegmentDndId: selectCustomRouteActiveSegment(state)?.dndId,
    isInCutMode: state.customRoute.cutData !== undefined,
    isTrailMap: state.customRoute.isTrailMap
  }));

  const [animationProgress, setAnimationProgress] = useState<number | undefined>(undefined);
  // Indexes of paths that will be animated. These will be rendered in a single source.
  const [animatedPathIndexes, setAnimatedPathIndexes] = useState<number[]>([]);
  // Indexes of paths that won't be animated. These will be rendered in a single source.
  const [staticPathIndexes, setStaticPathIndexes] = useState<number[]>([]);
  // The full paths being dragged. Paths are stored instead of indexes so that they can be displayed while the new path is being animated in even though they are no longer part of the segment.
  const [draggedPaths, setDraggedPaths] = useState<CustomRoutePath[]>([]);
  const isSafariBrowser = isSafari();

  // Refs used to track changes between renders
  const prevDragMarkerRef = useRef<MarkerProperties | undefined>(undefined);
  const prevPathsRef = useRef<CustomRoutePath[] | undefined>(undefined);
  const animationControlsRef = useRef<AnimationPlaybackControls | undefined>(undefined);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const prevUndoStackLengthRef = useRef<number | undefined>(undefined);
  const prevRedoStackLengthRef = useRef<number | undefined>(undefined);
  const lastSourceRefreshRef = useRef<number>(0);
  const pathsHashRef = useRef<string>('');

  const [, animate] = useAnimate();
  const { freeDrawPoints, dragMarker } = useContext(DrawControlsContext);

  const createPathsHash = useCallback((paths: CustomRoutePath[]) => {
    return paths
      .map(path => {
        const firstPoint = path.points[0];
        const lastPoint = path.points[path.points.length - 1];
        return `${path.points.length}-${firstPoint?.[0]}-${firstPoint?.[1]}-${lastPoint?.[0]}-${lastPoint?.[1]}`;
      })
      .join('|');
  }, []);

  const forceRefreshSources = useCallback(
    (specificSources?: string[]) => {
      if (!map) {
        return;
      }

      const currentTime = Date.now();

      if (currentTime - lastSourceRefreshRef.current < 100) {
        return;
      }

      const sourcesToRefresh = specificSources || [ANIMATION_UNIQUE_ID, DRAW_UNIQUE_ID, UNIQUE_ID];

      requestAnimationFrame(() => {
        sourcesToRefresh.forEach(sourceId => {
          const source = map.getSource(`polyline-source-${sourceId}`);
          if (source) {
            try {
              const data = source.serialize().data;
              (source as mapboxgl.GeoJSONSource).setData(data);

              if (!data || (data.features && data.features.length === 0)) {
                (source as mapboxgl.GeoJSONSource).setData({ type: 'FeatureCollection', features: [] });
              }
            } catch (error) {
              console.log(`Failed to refresh source ${sourceId}:`, error);
            }
          }
        });
        lastSourceRefreshRef.current = Date.now();
      });
    },
    [map]
  );

  const opacity = selectedWaypointId !== undefined ? 0.5 : undefined;

  const { drawData, drawOpacity } = useMemo(() => {
    if (!segment) {
      return {
        drawData: featureCollection<LineString, LineProperties>([])
      };
    }

    let drawFeatures: Feature<LineString, LineProperties>[] = [];
    let drawOpacity;

    if (draggedPaths.length > 0) {
      drawFeatures = getLineStrings({ ...segment, paths: draggedPaths });
      drawOpacity = 0.5;
    } else if (freeDrawPoints) {
      drawFeatures = getLineStrings({ ...segment, paths: [{ points: freeDrawPoints, freeDraw: true }] });
    }

    return {
      drawData: featureCollection<LineString, LineProperties>(drawFeatures),
      drawOpacity
    };
    // Only depend on draggedPaths and freeDrawPoints since we know they will be updated after the segment changes
  }, [freeDrawPoints, draggedPaths]);

  const animationData = useMemo(() => {
    if (!segment || animatedPathIndexes.length === 0) {
      return featureCollection<LineString, LineProperties>([]);
    }

    return combinedPathsFeature(segment, animatedPathIndexes);
    // Only depend on animatedPathsProgress since we know it will be updated after the segment changes
  }, [animatedPathIndexes]);

  const data = useMemo(() => {
    if (!segment) {
      return featureCollection<LineString, LineProperties>([]);
    }

    const features = getLineStrings(segment);
    return featureCollection<LineString, LineProperties>(features.filter(feature => staticPathIndexes.includes(feature.properties.pathIndex)));
    // Only depend on staticPathIndexes since we know it will be updated after the segment changes
  }, [staticPathIndexes]);

  const cancelAnimation = useCallback(() => {
    if (animationControlsRef.current) {
      animationControlsRef.current.stop();
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  }, []);

  const animatePaths = useCallback(
    (paths, pathIndexes: number[], duration: number) => {
      cancelAnimation();

      // Initialize animation state
      setAnimatedPathIndexes([...pathIndexes]);

      // Add the animated paths to the static paths and clear the animation ref
      const end = () => {
        setStaticPathIndexes(paths.map((_, index) => index));
        animationControlsRef.current = undefined;
      };

      // Reset animation state after the animation has been ended.
      // This is kept in a separate function so that this can be run after the animation has completed to prevent flickering.
      const cleanup = () => {
        setAnimatedPathIndexes([]);
        setAnimationProgress(undefined);
        animationFrameRef.current = undefined;
      };

      animationControlsRef.current = animate(0, 1, {
        duration: duration / 1000,
        ease: 'easeOut',
        onUpdate: value => {
          setAnimationProgress(value);
        },
        onStop: () => {
          end();
          cleanup();
        },
        onComplete: () => {
          end();

          // Wait for the next animation frame after the animation has ended to run cleanup to reduce the chance of flickering.
          // Attempting to run these updates in a separate useEffect or after updating static paths inside flushSync did not prevent flickering.
          // Perhaps the way react-map-gl handles source updates does not guarantee the source to be re-rendered on the map by the end of a React render cycle.
          animationFrameRef.current = requestAnimationFrame(() => {
            cleanup();
          });

          if (paths) {
            prevPathsRef.current = JSON.parse(JSON.stringify(paths));
          }
        }
      });
    },
    [animate, cancelAnimation]
  );

  // Reset change tracking refs when the active segment changes or when entering/exiting cut mode
  useEffect(() => {
    cancelAnimation();
    setAnimatedPathIndexes([]);
    setAnimationProgress(undefined);
    setDraggedPaths([]);
    setStaticPathIndexes([]);
    prevPathsRef.current = undefined;
    prevDragMarkerRef.current = undefined;
    prevUndoStackLengthRef.current = undefined;
    prevRedoStackLengthRef.current = undefined;
    pathsHashRef.current = '';
  }, [activeSegmentIndex, cancelAnimation, isInCutMode]);

  // Perform animations when changes are detected
  useEffect(() => {
    if (!segment) {
      cancelAnimation();
      setAnimatedPathIndexes([]);
      setAnimationProgress(undefined);
      setDraggedPaths([]);
      prevPathsRef.current = undefined;
      prevDragMarkerRef.current = undefined;
      setStaticPathIndexes(prev => (prev.length > 0 ? [] : prev));
      return;
    }

    const paths = segment.paths;
    const pathIndexes = paths.map((_, index) => index);

    // This can be used to return early if no animations are needed
    const updatePaths = () => {
      prevPathsRef.current = JSON.parse(JSON.stringify(paths));
      setStaticPathIndexes(pathIndexes);
    };

    const isMarkerBeingDragged = !!dragMarker;
    const isDragStart = isMarkerBeingDragged && prevDragMarkerRef.current === undefined;
    const isDragComplete = !isMarkerBeingDragged && prevDragMarkerRef.current !== undefined;

    // TODO: DISCO-16259: If we switch to patches for undo/redo we can probably detect these without storing the previous paths
    // A path was deleted if the number of paths is less than the previous number of paths
    const isPathDeletion = !!prevPathsRef.current && paths.length < prevPathsRef.current.length;
    // The last remaining path in a segment was deleted if the segment has a single path with a single point and the previous single path had more than one point
    const isOnlyPathDeletion =
      paths.length === 1 && paths[0].points.length === 1 && prevPathsRef.current?.length === 1 && prevPathsRef.current[0].points.length > 1;
    // A path was added if the number of paths is greater than the previous number of paths
    const isPathAddition = prevPathsRef.current && paths.length > prevPathsRef.current.length && paths.length > 1;
    // The first path was drawn when the segment has a single path with more than one point and the previous single path had only one point
    const isFirstPathAddition =
      paths.length === 1 && prevPathsRef.current?.length === 1 && paths[0].points.length > 1 && prevPathsRef.current[0].points.length === 1;

    // Don't animate when going into or out of cut mode
    if (isInCutMode) {
      updatePaths();
      return;
    }

    // Don't animate path deletion
    if (isPathDeletion || isOnlyPathDeletion) {
      if (prevPathsRef.current) {
        logCustomRouteUpdated({
          custom_route_action_taken: 'remove point',
          custom_route_mode: isSmartRouting ? 'smart draw' : 'free draw'
        });
      }
      updatePaths();
      return;
    }

    if (isDragStart) {
      cancelAnimation();
      if (prevPathsRef.current) {
        logCustomRouteUpdated({
          custom_route_action_taken: 'dragged',
          custom_route_mode: isSmartRouting ? 'smart draw' : 'free draw'
        });
      }

      // Display the old paths to a marker while it is being dragged
      const newDraggedPaths: CustomRoutePath[] = [];
      const { isStart, isEnd, pathIndex } = getMarkerSelectionProperties(dragMarker, selectedHandleIndex);

      if (isStart) {
        newDraggedPaths.push(paths[0]);
      } else if (isEnd) {
        newDraggedPaths.push(paths[paths.length - 1]);
      } else {
        newDraggedPaths.push(paths[pathIndex - 1]);
        newDraggedPaths.push(paths[pathIndex]);
      }

      setDraggedPaths(newDraggedPaths);
    } else if (isDragComplete) {
      // Animate the new paths to the dragged marker's new position
      const { pathIndex, isStart, isEnd } = getMarkerSelectionProperties(prevDragMarkerRef.current!, selectedHandleIndex);

      const pathIndexes: number[] = [];

      if (isStart) {
        pathIndexes.push(0);
      } else if (isEnd) {
        pathIndexes.push(paths.length - 1);
      } else {
        pathIndexes.push(pathIndex, pathIndex - 1);
      }

      const animatedIndexes = pathIndexes.filter(index => !paths[index].freeDraw);
      // A drop does not always change the route: free draw rejects a point too far from the previous one, and smart
      // routing can come back with nothing. useDragMarker clears its pending marker either way, so this branch still runs
      // on those. Hiding the geometry and animating it back in unchanged would blank the path for the animation's length.
      const hasNewGeometry = animatedIndexes.some(index => !isEqual(prevPathsRef.current?.[index], paths[index]));

      if (hasNewGeometry) {
        // Take the animated paths out of the static source now instead of leaving it to animatePaths' end(), which runs
        // when the animation finishes, so the old line goes before the new one is drawn in rather than after. The data
        // memo below deliberately does not depend on segment, so changing staticPathIndexes is the only thing that makes
        // it recompute against the new geometry. end() puts the full set back, and runs on interruption as well as
        // completion, so the animated paths cannot be left out of the static source.
        setStaticPathIndexes(paths.map((_, index) => index).filter(index => !animatedIndexes.includes(index)));
        animatePaths(paths, animatedIndexes, getAnimationDuration(pathIndexes, segment, map));
      } else {
        updatePaths();
      }

      setDraggedPaths([]);
    } else if (!isMarkerBeingDragged && !freeDrawPoints) {
      if (!isPathAddition && !isFirstPathAddition) {
        updatePaths();
        return;
      }

      let addedIndexes = [0];
      // If this is not the first path addition, we need to find the indexes of the added paths
      if (isPathAddition) {
        // TODO: DISCO-16259: If we switch to patches for undo/redo we can probably determine the added paths without storing the previous paths
        let prevPathsIndex = 0;
        addedIndexes = paths.reduce((acc, path, index) => {
          if (isEqual(prevPathsRef.current?.[prevPathsIndex], path)) {
            // If the current path is equal to the previous path at the same index
            // We want the next comparison to be at the next index of both the current and previous paths
            prevPathsIndex++;
          } else {
            // If the current path is not equal to the previous path at the same index, it is a new path
            // We want the next comparison to be between the next index of the current paths and the same index of the previous paths to see if additional paths were inserted
            acc.push(index);
          }
          return acc;
        }, [] as number[]);
      }

      // Animate any added paths that are not free drawn
      const animatedIndexes = addedIndexes.filter(index => !paths[index].freeDraw);

      if (animatedIndexes.length === 0) {
        updatePaths();
        return;
      }

      if (prevPathsRef.current) {
        logCustomRouteUpdated({
          custom_route_action_taken: 'segment added',
          custom_route_mode: isSmartRouting ? 'smart draw' : 'free draw'
        });
      }
      prevPathsRef.current = JSON.parse(JSON.stringify(paths));
      animatePaths(paths, animatedIndexes, getAnimationDuration(animatedIndexes, segment, map));
    }

    prevDragMarkerRef.current = dragMarker ? { ...dragMarker } : undefined;
  }, [map, cancelAnimation, animatePaths, segment, dragMarker, selectedHandleIndex, freeDrawPoints, isSmartRouting]);

  // When which segment is active changes fit the map to the segment bounds if it is not fully contained in the view.
  // Keyed on dndId rather than the segment, so editing a segment doesn't re-frame it. dndIds are handed out in
  // ascending order per store, so a segment from a different route always gets a new key.
  useFitBoundsOnce('customRouteActiveSegment', activeSegmentDndId, fitBoundsMap => {
    if (!segment || segment.paths.length === 0 || (segment.paths.length === 1 && segment.paths[0].points.length < 2) || isTrailMap) {
      return;
    }

    const bounds = getSegmentsBounds([segment.paths]);
    if (bounds) {
      fitBoundsMap.fitBounds(bounds);
    }
  });

  useEffect(() => {
    if (!map || !segment || !isSafariBrowser) {
      return;
    }

    // Force Safari to refresh by temporarily hiding and showing the sources
    const forceRefresh = () => {
      const sources = [ANIMATION_UNIQUE_ID, DRAW_UNIQUE_ID, UNIQUE_ID];

      sources.forEach(sourceId => {
        const source = map.getSource(`polyline-source-${sourceId}`);
        if (source) {
          (source as mapboxgl.GeoJSONSource).setData(source.serialize().data);
        }
      });
    };

    // Giving Safari time to process the state updates before refreshing
    const frameId = requestAnimationFrame(forceRefresh);

    return () => cancelAnimationFrame(frameId);
  }, [map, routeActivityType, segment?.paths, activeSegmentIndex]);

  // Force source refresh when state changes rapidly to prevent stale polyline rendering
  useEffect(() => {
    if (!map || !segment) {
      prevUndoStackLengthRef.current = undoStackLength;
      prevRedoStackLengthRef.current = redoStackLength;
      pathsHashRef.current = '';
      return undefined;
    }

    const isUndoOperation = prevUndoStackLengthRef.current !== undefined && undoStackLength < prevUndoStackLengthRef.current;
    const isRedoOperation = prevRedoStackLengthRef.current !== undefined && redoStackLength < prevRedoStackLengthRef.current;

    const currentPathsHash = createPathsHash(segment.paths);
    const pathsChanged = pathsHashRef.current !== '' && pathsHashRef.current !== currentPathsHash && animationProgress === undefined;
    const shouldRefresh = isUndoOperation || isRedoOperation || pathsChanged;

    if ((isUndoOperation || isRedoOperation) && animationProgress !== undefined) {
      cancelAnimation();
      setAnimatedPathIndexes([]);
      setAnimationProgress(undefined);
      setDraggedPaths([]);
    }

    if (shouldRefresh) {
      const sourcesToRefresh =
        isUndoOperation || isRedoOperation
          ? [ANIMATION_UNIQUE_ID, DRAW_UNIQUE_ID, UNIQUE_ID]
          : (() => {
              const sources: string[] = [UNIQUE_ID];
              if (animatedPathIndexes.length > 0) {
                sources.push(ANIMATION_UNIQUE_ID);
              }
              if (freeDrawPoints && freeDrawPoints.length > 0) {
                sources.push(DRAW_UNIQUE_ID);
              }
              return sources;
            })();

      forceRefreshSources(sourcesToRefresh);
    }

    prevUndoStackLengthRef.current = undoStackLength;
    prevRedoStackLengthRef.current = redoStackLength;
    pathsHashRef.current = currentPathsHash;

    return undefined;
  }, [
    map,
    segment,
    undoStackLength,
    redoStackLength,
    animationProgress,
    forceRefreshSources,
    createPathsHash,
    animatedPathIndexes,
    freeDrawPoints,
    cancelAnimation
  ]);

  // Force re-render when segment changes to prevent Safari from using stale data
  const renderKey = useMemo(() => {
    if (activeSegmentIndex === undefined) {
      return 'no-segment';
    }
    return `active-segment-${activeSegmentIndex}`;
  }, [activeSegmentIndex]);

  // Note that we will always render all sources even if they are empty to prevent flickering when the source transitions between empty and non-empty data
  return (
    <>
      <PolylineSource
        key={renderKey}
        data={animationData}
        uniqueId={ANIMATION_UNIQUE_ID}
        hasSourcePaint={false}
        hideArrows={hideArrows}
        lineProgress={animationProgress}
        lineColor={segment ? getLineColor(segment.color) : undefined}
        lineOutlineColor={segment ? getLineOutlineColor(segment.color) : undefined}
      />
      <PolylineSource
        data={drawData}
        uniqueId={DRAW_UNIQUE_ID}
        beforeUniqueId={ANIMATION_UNIQUE_ID}
        opacity={drawOpacity}
        hasSourcePaint={true}
        hideArrows={hideArrows}
        // The key is set based on freeDrawPoints to force React to remount the PolylineSource when transitioning between empty and non-empty draw states
        // This prevents rendering issue with stale data when the source transitions between having no points and having points, ensuring the map updates correctly
        key={freeDrawPoints && freeDrawPoints.length > 0 ? 'draw-active' : 'draw-empty'}
      />
      <PolylineSource
        data={data}
        uniqueId={UNIQUE_ID}
        beforeUniqueId={DRAW_UNIQUE_ID}
        opacity={opacity}
        hasSourcePaint={true}
        hideArrows={hideArrows}
        // Remounting flushes stale source data onto the map, the same reason the draw source above is keyed on its
        // empty/non-empty state. Length is a coarse proxy though: a same-length change of indexes does not remount and
        // leans on the data memo recomputing instead.
        key={`${renderKey}-main-${staticPathIndexes.length}-${reverseCount}`}
      />
    </>
  );
};

export default ActivePolyline;
